import 'dayjs/locale/en-ca'
import 'dayjs/locale/fr'
import 'dayjs/locale/fr-ca'

import { useMemo } from 'react'
import { useCollectionData } from 'react-firebase-hooks/firestore'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { sortBy } from 'lodash-es'

import {
  type ConfigurationHistoryEvent,
  configurationHistoryQuery,
  configurationHistoryQueryOptions,
} from '@/model/firestore/useConfigurationHistory'
import { operatingRegionsQuery, regionsQuery } from '@/state/regions/regions'
import { filterActiveRegions } from '@/state/regions/regionUtils'

import { buildClosedEveningData, type RegionWithEvenings, START_HOUR, useChartNow } from './chartUtils'
import { type ConfigurationHistoryStats, useConfigurationHistoryStats } from './useConfigurationHistoryStats'

/**
 * Normalize locale string for dayjs compatibility.
 */
const normalizeDayjsLocale = (locale: string): string => {
  const lower = locale.toLowerCase()
  if (lower === 'fr') return 'fr-ca'
  if (lower === 'en') return 'en-ca'
  return lower
}

// Special value for "now" (current operating evening)
export const NOW_DATE = 'now'

export interface AvailableRegion {
  regionId: string
  regionName: string
}

export interface AvailableDate {
  date: string // YYYY-MM-DD or 'now'
  label: string
}

export interface UseConfigurationHistoryPageOptions {
  regionId: string | undefined
  operatingDate: string | undefined // YYYY-MM-DD or 'now'
  locale: string
  nowLabel: string
}

export interface UseConfigurationHistoryPageReturn {
  availableRegions: AvailableRegion[]
  availableDates: AvailableDate[]
  selectedRegionId: string | undefined
  selectedOperatingDate: string | undefined
  stats: ConfigurationHistoryStats
  events: ConfigurationHistoryEvent[]
  eveningStartTime: Date | undefined
  eveningEndTime: Date | undefined
  isLoading: boolean
}

export const useConfigurationHistoryPage = ({
  regionId,
  operatingDate,
  locale: rawLocale,
  nowLabel,
}: UseConfigurationHistoryPageOptions): UseConfigurationHistoryPageReturn => {
  const locale = normalizeDayjsLocale(rawLocale)
  const now = useChartNow()

  // Fetch all regions and operating regions
  const { data: regionsSnapshot } = useSuspenseQuery(regionsQuery())
  const { data: operatingRegionsSnapshot } = useSuspenseQuery(operatingRegionsQuery())

  // Build available regions list (non-test regions in prod, all regions in dev)
  const availableRegions = useMemo(
    (): AvailableRegion[] =>
      sortBy(
        regionsSnapshot.data
          .filter((region) => !region.test || import.meta.env.DEV)
          .map((region) => ({
            regionId: region._id,
            regionName: region.name ?? 'Unknown',
          })),
        'regionName'
      ),
    [regionsSnapshot.data]
  )

  // Check if selected region is currently operating
  const selectedRegionOperatingDate = useMemo(
    () =>
      regionId
        ? filterActiveRegions(operatingRegionsSnapshot.data).find((r) => r._id === regionId)?.currentOperatingDate
        : undefined,
    [regionId, operatingRegionsSnapshot.data]
  )

  const isSelectedRegionOperating = !!selectedRegionOperatingDate

  // Build closed evening data for the selected region
  const { closedDates } = useMemo(() => {
    if (!regionId) {
      return { closedDates: [], eveningIdsByDate: {}, eveningsByDateAndRegion: {} }
    }
    const regionData = regionsSnapshot.data.filter((r) => r._id === regionId) as RegionWithEvenings[]
    return buildClosedEveningData(regionData, locale)
  }, [regionId, regionsSnapshot.data, locale])

  // Combine "now" option with closed dates
  const availableDates = useMemo((): AvailableDate[] => {
    const result: AvailableDate[] = []
    if (isSelectedRegionOperating) {
      result.push({ date: NOW_DATE, label: nowLabel })
    }
    result.push(...closedDates)
    return result
  }, [isSelectedRegionOperating, closedDates, nowLabel])

  // Resolve the actual operating date to query
  const resolvedOperatingDate = useMemo((): string | undefined => {
    if (!regionId || !operatingDate) return undefined
    if (operatingDate === NOW_DATE) {
      return selectedRegionOperatingDate?.date
    }
    return operatingDate
  }, [regionId, operatingDate, selectedRegionOperatingDate])

  // Determine if we're viewing a live evening
  const isLive = operatingDate === NOW_DATE

  // For live evenings: use Firestore snapshot listener (realtime updates)
  const liveQuery = useMemo(
    () =>
      isLive && regionId && resolvedOperatingDate ? configurationHistoryQuery(regionId, resolvedOperatingDate) : null,
    [isLive, regionId, resolvedOperatingDate]
  )
  const [liveData, liveLoading] = useCollectionData<ConfigurationHistoryEvent>(liveQuery)

  // For historical evenings: use TanStack Query (one-time read)
  const { data: historicalData = [], isLoading: historicalLoading } = useQuery({
    ...configurationHistoryQueryOptions({
      regionId: regionId ?? '',
      operatingDate: resolvedOperatingDate,
    }),
    enabled: !isLive && !!regionId && !!resolvedOperatingDate,
    staleTime: Infinity, // Historical data doesn't change
  })

  // Combine the data sources based on mode
  const configurationHistory = isLive ? liveData : historicalData
  const isLoading = isLive ? liveLoading : historicalLoading

  const events = useMemo(() => configurationHistory ?? [], [configurationHistory])

  // Calculate evening start and end times
  // For configuration history, use the earliest event timestamp as the start time
  // (when the evening was actually opened), not the scheduled customer-facing start time
  const { eveningStartTime, eveningEndTime } = useMemo(() => {
    if (!resolvedOperatingDate) {
      return { eveningStartTime: undefined, eveningEndTime: undefined }
    }

    // Find the earliest event timestamp (when the evening was actually opened)
    const sortedEvents = [...events].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    const earliestEventTime = sortedEvents.length > 0 ? sortedEvents[0].timestamp : null

    let startTime: Date

    if (earliestEventTime) {
      // Use the earliest event timestamp as the actual opening time
      startTime = earliestEventTime
    } else if (isLive && selectedRegionOperatingDate?.startTimestamp) {
      // Fall back to scheduled start time if no events yet
      startTime = selectedRegionOperatingDate.startTimestamp
    } else {
      // Fall back to default hours for historical evenings with no events
      startTime = dayjs(resolvedOperatingDate).hour(START_HOUR).minute(0).second(0).toDate()
    }

    const endTime = isLive
      ? new Date(Math.max(now.getTime(), startTime.getTime()))
      : dayjs(resolvedOperatingDate).add(1, 'day').hour(4).minute(0).second(0).toDate()

    return { eveningStartTime: startTime, eveningEndTime: endTime }
  }, [resolvedOperatingDate, isLive, now, selectedRegionOperatingDate, events])

  // Calculate stats from the history
  const stats = useConfigurationHistoryStats(events, eveningStartTime, eveningEndTime)

  return {
    availableRegions,
    availableDates,
    selectedRegionId: regionId,
    selectedOperatingDate: operatingDate,
    stats,
    events,
    eveningStartTime,
    eveningEndTime,
    isLoading,
  }
}

export { formatDuration, type TimeSegment, type TypeStats } from './useConfigurationHistoryStats'
